import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { SelectOption } from "../../core/models/select-option.model";
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-autosuggest-dropdown-input',
  templateUrl: './autosuggest-dropdown-input.component.html',
  styleUrls: ['./autosuggest-dropdown-input.component.scss']
})
export class AutosuggestDropdownInputComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() selectValueChanged = new EventEmitter();
  @Output() cleared = new EventEmitter();
  @ViewChild('input') input: ElementRef;
  @ViewChild('autoCompleteParent') autoCompleteParent: ElementRef;

  @Input('parentForm') parentForm: UntypedFormGroup;
  @Input('controlName') controlName: string;
  @Input('options') options: SelectOption[] = [];
  @Input('className') className = '';
  @Input('placeholder') placeholder = '';
  @Input('required') required = false;
  @Input('disabled') disabled = false;

  filteredOptions: SelectOption[];

  displayValue: string;
  form: UntypedFormGroup;
  selectedOption: SelectOption = null;
  showDropdown = false;
  private parentControlStatusSub: Subscription;

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (this.input !== undefined) {
      if (!(this.input.nativeElement.contains(event.target) || this.autoCompleteParent.nativeElement.contains(event.target))) {
        this.showDropdown = false;
      }
    }
  }

  constructor() { }

  ngOnInit() {
    this.form = new UntypedFormGroup({
      input: new UntypedFormControl('', [this.optionSelectedValidator.bind(this)])
    });
    let parentControl = this.parentForm.get(this.controlName);
    if (parentControl !== null) {
      let isParentControlDisabled = this.parentForm.controls[this.controlName].disabled;
      if (isParentControlDisabled) {
        this.disable();
      } else {
        this.enable();
      }
      // Subscribe to status changes
      this.parentControlStatusSub = parentControl.statusChanges.subscribe(() => {
        let value = parentControl.value;
        if (value && this.selectedOption === null) {
          this.setSelectedOption(value);
        }

        if (parentControl.disabled && !this.disabled) {
          this.disable();
        } else if (!parentControl.disabled && this.disabled) {
          this.enable();
        }
      });
    }
  }

  ngAfterViewInit() {
    this.filteredOptions = this.options;
  }

  reset(): void {
    this.parentForm.patchValue({[this.controlName]: ''});
    this.selectedOption = null;
    this.displayValue = '';
    if (this.input)
      this.input.nativeElement.value = '';
  }

  disable() {
    this.form.get('input').disable();
    this.disabled = true;
  }

  enable() {
    this.form.get('input').enable();
    this.disabled = false;
  }

  optionSelectedValidator(control: UntypedFormControl): { [s: string]: boolean } | null {
    if (this.selectedOption === null || this.selectedOption === undefined) {
      return { 'noOptionSelected': true };
    }

    return null;
  }

  onInputValueChanged(event: any) {
    let value = event.target.value;

    if (value !== null && value !== '') {
      this.filteredOptions = this._filter(event.target.value);
    } else {
      this.filteredOptions = this.options;
    }
  }

  onClick(): void {
    if (!this.disabled) {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        setTimeout(() => {
          if (this.input)
          {
            this.input.nativeElement.value = '';
            this.input.nativeElement.focus();
          }

          this.filteredOptions = this.options;
        }, 500);
      }
    }
  }

  /**
   * Called when a country is selected from the country dropdown
   * @param option
   */
  selectOption(option: SelectOption) {
    this.selectedOption = option;
    this.parentForm.patchValue({ [this.controlName]: option.value });
    this.displayValue = option.text.substring(0, 30) + (option.text.length > 30 ? '...' : '');
    this.showDropdown = false;
    this.selectValueChanged.emit(option.value);
  }

  setOptions(options: SelectOption[]) {
    this.options = options;
  }

  setSelectedOption(value: string) {
    let option = this.findOptionByValue(value);
    if (option !== null) {
      this.selectOption(option);
    }
  }

  onClear() {
    this.selectedOption = null;
    this.parentForm.patchValue({ [this.controlName]: '' });
    this.filteredOptions = this.options;
    this.cleared.emit();
  }

  findOptionByValue(value: string): SelectOption {
    let foundOption: SelectOption = null;

    this.options.forEach(option => {
      if (value?.toLowerCase() === option.value?.toLowerCase()) {
        foundOption = option;
      }
    });

    return foundOption;
  }

  /**
   * Handles filtering the field
   * @param name
   * @private
   */
  private _filter(name: string): SelectOption[] {
    const filterValue = name.toLowerCase();

    return this.options.filter(option => option.text.toLowerCase().includes(filterValue));
  }

  ngOnDestroy() {
    if (this.parentControlStatusSub) {
      this.parentControlStatusSub.unsubscribe();
    }
  }
}
